<div class="usage">

  <header class="usage__head">
    <div class="usage__title">
      <mat-icon fontSet="material-icons-round" class="usage__mark" aria-hidden="true">auto_awesome</mat-icon>
      <div>
        <h1>{{ 'Consumi dell\'assistente AI' | translate }}</h1>
        @if (summary) {
          <p class="usage__subtitle">{{ summary.domain.name }}, {{ periodLabel }}</p>
        }
      </div>
    </div>

    <div class="period" role="group" [attr.aria-label]="'Periodo' | translate">
      <div class="period__presets">
        @for (option of presets; track option.value) {
          <button type="button" class="period__preset" [class.is-active]="preset === option.value"
            [attr.aria-pressed]="preset === option.value" (click)="applyPreset(option.value)">
            {{ option.label | translate }}
          </button>
        }
      </div>
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="period__range" [class.is-active]="preset === 'custom'">
        <mat-label>{{ 'Dal – al' | translate }}</mat-label>
        <mat-date-range-input [formGroup]="range" [rangePicker]="rangePicker">
          <input matStartDate formControlName="from" [placeholder]="'Inizio' | translate">
          <input matEndDate formControlName="to" [placeholder]="'Fine' | translate">
        </mat-date-range-input>
        <mat-datepicker-toggle matIconSuffix [for]="rangePicker"></mat-datepicker-toggle>
        <mat-date-range-picker #rangePicker (closed)="onRangeClosed()"></mat-date-range-picker>
      </mat-form-field>
    </div>
  </header>

  @if (loading) {
    <mat-progress-bar mode="indeterminate" class="usage__progress"></mat-progress-bar>
  }

  @if (error) {
    <section class="notice notice--error" role="alert">
      <mat-icon fontSet="material-icons-round" aria-hidden="true">{{ error.code === 'unavailable' ? 'cloud_off' : 'lock' }}</mat-icon>
      <div>
        <p>{{ error.message | translate }}</p>
        @if (error.code === 'unavailable') {
          <button mat-stroked-button type="button" (click)="load()">{{ 'Riprova' | translate }}</button>
        }
      </div>
    </section>
  } @else if (summary) {

    @if (!summary.module_enabled) {
      <section class="notice">
        <mat-icon fontSet="material-icons-round" aria-hidden="true">power_settings_new</mat-icon>
        <p>{{ 'L\'assistente è spento per questa azienda: qui sotto trovi lo storico dei consumi.' | translate }}</p>
      </section>
    }

    <!-- budget: lo stesso contatore che blocca le chat quando si esaurisce -->
    <section class="budget" [attr.data-tone]="budgetTone" [attr.aria-label]="'Budget del periodo' | translate">
      @if (summary.budget; as budget) {
        <div class="budget__figures">
          <p class="budget__spent">
            <span class="budget__amount">{{ compactTokens(budget.used_tokens) }}</span>
            <span class="budget__of">{{ 'token usati su' | translate }} {{ compactTokens(budget.max_tokens) }} ({{ percent(budgetRatio) }})</span>
          </p>
          <p class="budget__left">
            @if (budgetTone === 'over') {
              {{ 'Budget esaurito: l\'assistente è in pausa fino all\'azzeramento.' | translate }}
            } @else {
              {{ 'Restano' | translate }} <strong>{{ compactTokens(budgetLeft) }} {{ 'token' | translate }}</strong>
            }
            @if (resetLabel) {
              <span class="budget__reset">{{ resetLabel }}</span>
            }
          </p>
        </div>
        <div class="budget__track" role="meter" aria-valuemin="0" aria-valuemax="1"
          [attr.aria-valuenow]="budgetRatio" [attr.aria-valuetext]="percent(budgetRatio)">
          <span class="budget__fill" [style.width.%]="budgetRatio * 100"></span>
        </div>
      }
    </section>

    <!-- totali del periodo selezionato -->
    <dl class="totals">
      <div class="totals__item">
        <dt>{{ 'Richieste' | translate }}</dt>
        <dd>{{ tokens(summary.totals.requests) }}</dd>
      </div>
      <div class="totals__item">
        <dt>{{ 'Token usati' | translate }}</dt>
        <dd [matTooltip]="tokens(summary.totals.input_tokens) + ' ' + ('in ingresso' | translate) + ', ' + tokens(summary.totals.output_tokens) + ' ' + ('generati' | translate)">
          {{ compactTokens(summary.totals.total_tokens) }}
        </dd>
      </div>
      @if (showCosts) {
        <div class="totals__item">
          <dt>{{ 'Costo' | translate }}</dt>
          <dd>{{ money(summary.totals.cost_usd) }}</dd>
        </div>
      } @else {
        <div class="totals__item">
          <dt>{{ 'Token per richiesta' | translate }}</dt>
          <dd>{{ compactTokens(averageTokens) }}</dd>
        </div>
      }
      <div class="totals__item">
        <dt>{{ 'Utenti attivi' | translate }}</dt>
        <dd>{{ summary.totals.users }}</dd>
      </div>
    </dl>

    <div class="usage__grid">
      <section class="panel trend">
        <h2>{{ 'Token per giorno' | translate }}</h2>
        @if (hasDailyActivity) {
          <div class="trend__chart" role="img" [attr.aria-label]="'Token usati per giorno nel periodo' | translate">
            @for (day of summary.by_day; track day.date; let index = $index, count = $count) {
              <div class="trend__day" [matTooltip]="dayTooltip(day)" matTooltipPosition="above">
                <span class="trend__bar" [class.is-empty]="!day.total_tokens" [style.height]="barHeight(day.total_tokens)"></span>
                <span class="trend__label">{{ axisLabel(index, count, day.date) }}</span>
              </div>
            }
          </div>
        } @else {
          <p class="panel__empty">{{ 'Nessun utilizzo in questo periodo.' | translate }}</p>
        }
      </section>

      <section class="panel people">
        <h2>{{ 'Chi lo usa' | translate }}</h2>
        @if (summary.by_user.length) {
          <ol class="people__list">
            @for (row of summary.by_user; track row.user.id) {
              <li class="people__row">
                <span class="avatar" aria-hidden="true">{{ initials(row.user.name) }}</span>
                <div class="people__body">
                  <div class="people__line">
                    <span class="people__name">{{ row.user.name }}</span>
                    <span class="people__cost">{{ showCosts ? money(row.cost_usd) : compactTokens(row.total_tokens) + ' token' }}</span>
                  </div>
                  <div class="people__share"><span [style.width]="userShare(row.total_tokens)"></span></div>
                  <span class="people__meta">
                    {{ row.requests }} {{ (row.requests === 1 ? 'richiesta' : 'richieste') | translate }},
                    {{ 'ultima' | translate }} {{ when(row.last_used_at) | lowercase }}
                  </span>
                </div>
              </li>
            }
          </ol>
        } @else {
          <p class="panel__empty">{{ 'Nessuno ha usato l\'assistente in questo periodo.' | translate }}</p>
        }
      </section>
    </div>

    <!-- attività: una riga per richiesta -->
    <section class="panel activity">
      <div class="activity__head">
        <h2>{{ 'Attività' | translate }}</h2>
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="activity__filter">
          <mat-label>{{ 'Utente' | translate }}</mat-label>
          <mat-select [formControl]="userFilter">
            <mat-option [value]="null">{{ 'Tutti gli utenti' | translate }}</mat-option>
            @for (user of summary.users; track user.id) {
              <mat-option [value]="user.id">{{ user.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>

      @if (loadingLogs) {
        <mat-progress-bar mode="indeterminate" class="activity__progress"></mat-progress-bar>
      }

      @if (logs?.data?.length) {
        <ol class="log">
          @for (log of logs!.data; track log.id) {
            <li class="log__item">
              <div class="log__meta">
                <time [attr.datetime]="log.started_at">{{ when(log.started_at) }}</time>
                <span class="log__user">{{ log.user.name }}</span>
              </div>
              <div class="log__body">
                @if (log.conversation_deleted) {
                  <p class="log__request log__request--gone">{{ 'Conversazione cancellata dopo il periodo di conservazione' | translate }}</p>
                } @else {
                  @if (log.request) {
                    <q class="log__request">{{ log.request }}</q>
                  } @else if (!log.attachments?.length) {
                    <p class="log__request log__request--gone">{{ 'Richiesta senza testo' | translate }}</p>
                  }
                  @if (log.attachments?.length) {
                    <ul class="log__files" [attr.aria-label]="'File allegati' | translate">
                      @for (attachment of log.attachments; track $index) {
                        <li class="log__file">
                          <mat-icon class="s-16" fontSet="material-icons-round" aria-hidden="true">attach_file</mat-icon>
                          <span class="log__file-name">{{ attachment.name }}</span>
                          <span class="log__file-meta">{{ attachment.lines }} {{ (attachment.lines === 1 ? 'riga' : 'righe') | translate }}</span>
                        </li>
                      }
                    </ul>
                  }
                }
                <div class="log__context">
                  @if (whereLabel(log); as where) {
                    <span class="log__where">
                      <span class="log__where-type">{{ where | translate }}</span>
                      @if (entityLink(log); as link) {
                        <a class="log__where-link" [routerLink]="link">{{ log.entity!.title }}</a>
                      } @else if (log.entity) {
                        <span class="log__where-gone">{{ 'eliminato' | translate }}</span>
                      }
                    </span>
                  }
                  @for (action of log.actions; track $index) {
                    <span class="outcome" [attr.data-status]="action.status" [matTooltip]="action.title">
                      {{ actionLabel(action.status) | translate }}: {{ action.title }}
                    </span>
                  }
                </div>
              </div>
              <div class="log__cost" [matTooltip]="log.calls + ' ' + ((log.calls === 1 ? 'chiamata al modello' : 'chiamate al modello') | translate)">
                @if (showCosts) {
                  <span class="log__money">{{ money(log.cost_usd) }}</span>
                  <span class="log__tokens">{{ compactTokens(log.total_tokens) }} token</span>
                } @else {
                  <span class="log__money">{{ compactTokens(log.total_tokens) }} token</span>
                  <span class="log__tokens">{{ tokens(log.input_tokens) }} + {{ tokens(log.output_tokens) }}</span>
                }
              </div>
            </li>
          }
        </ol>
        @if (logs!.meta.total > perPage) {
          <mat-paginator [length]="logs!.meta.total" [pageSize]="perPage" [pageIndex]="logs!.meta.current_page - 1"
            [hidePageSize]="true" (page)="onPage($event)"></mat-paginator>
        }
      } @else if (!loadingLogs) {
        <p class="panel__empty">
          @if (userFilter.value) {
            {{ 'Questo utente non ha fatto richieste nel periodo.' | translate }}
          } @else {
            {{ 'Le richieste fatte all\'assistente compariranno qui, con chi le ha fatte e quanti token hanno usato.' | translate }}
          }
        </p>
      }
    </section>
  }
</div>
